import React from 'react';
import { ClickAwayListener } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { FilterDropdownProps } from './types';
import { useFilterDropdown } from './hooks/useFilterDropdown';
import { useApiFilterManager } from './hooks/useApiFilterManager';
import { useFiltersWithNames } from './hooks/useFilters';
import { useDynamicFilters } from './hooks/useDynamicFilters';
import { DynamicFilterRenderer } from './components/DynamicFilterRenderer';
import {
  FilterContainer,
  FilterContent,
  FilterButton,
  ScrollableContent,
  ActionButtonContainer,
  ActionButton
} from './styledComponents';
import {
  LoadingState,
  ErrorState
} from './components';
import {
  MOCK_API_FILTERS
} from './constants/filterConstants';

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  priceMin,
  priceMax,
  minInputValue,
  maxInputValue,
  priceErrorMessage,
  priceRangesMin,
  priceRangesMax,
  onMinInputChange,
  onMaxInputChange,
  onMinPriceSelect,
  onMaxPriceSelect,
  onMinInputClear,
  onMaxInputClear,
  onApply,
  onReset,
  selectedBedrooms = null,
  onBedroomSelect,
  selectedBathrooms = null,
  onBathroomSelect,
  areaMin,
  areaMax,
  areaMinInput,
  areaMaxInput,
  areaRangesMin,
  areaRangesMax,
  onAreaMinInputChange,
  onAreaMaxInputChange,
  onAreaMinSelect,
  onAreaMaxSelect,
  onAreaMinInputClear,
  onAreaMaxInputClear,
  selectedQuota,
  onQuotaSelect,
  selectedStatus,
  onStatusSelect,
  selectedFloor,
  onFloorSelect,
  selectedPublishedBy,
  onPublishedBySelect,
  selectedPostedDate,
  onPostedDateSelect,
  selectedView,
  onViewSelect,
  selectedDirection,
  onDirectionSelect,
  selectedAction,
  selectedCategory,
  apiFilters,
  enableApiFilters = true,
  showLoadingState = true,
  fallbackToMockData = true,
  showApiErrorMessage = true,
  onDynamicFilterChange,
  selectedLocation,
  selectedSubLocation,
  locationTypeId,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'th'; // Get current locale

  const {
    isOpen,
    expandedPanels,
    containerRef,
    sectionVisibility,
    isApiLoading,
    apiError,
    isApiError,
    toggleDropdown,
    closeDropdown,
    handlePanelChange,
  } = useFilterDropdown(selectedAction, selectedCategory, enableApiFilters, fallbackToMockData, locale);

  const { 
    filtersData 
  } = useFiltersWithNames(
    enableApiFilters ? (selectedAction ?? null) : null,
    enableApiFilters ? (selectedCategory ?? null) : null,
    locale // ส่ง locale ปัจจุบัน
  );

  const {
    genericFilterSelections,
    handleGenericFilterSelect,
    resetGenericSelections,
    getSelectedQueryParams
  } = useApiFilterManager(filtersData);

  const handleGenericFilterSelectWithCallback = (groupId: number, value: string | null, option: any) => {
    handleGenericFilterSelect(groupId, value, option);
    
    if (onDynamicFilterChange) {
      const optionId = value ? parseInt(value) : null;
      onDynamicFilterChange(groupId, optionId, option.query_params || {});
    }
  };
  
  const { dynamicFilters, getFilterLayout } = useDynamicFilters(
    filtersData,
    MOCK_API_FILTERS, // fallback filters
    selectedCategory, // pass selected category for filtering
    selectedLocation, // location state for distance filter condition
    selectedSubLocation, // sub-location state for distance filter condition  
    locationTypeId  // location type ID for distance filter condition
  );

  React.useEffect(() => {
    if (isOpen) {
    }
  }, [selectedAction, selectedCategory, filtersData.length, dynamicFilters.length, isApiLoading, isApiError, isOpen, enableApiFilters, fallbackToMockData, locale, filtersData, dynamicFilters]);

  const handleReset = () => {
    onReset();
    resetGenericSelections();
    closeDropdown();
  };

  const handleApply = () => {
    onApply(); // แค่เก็บค่า ไม่ search
    closeDropdown(); // ปิด dropdown
  };

  // สำหรับหุบ accordion หลังเลือก option ใน mobile
  const handleCollapseAfterSelect = (panelKey: string) => () => {
    // หุบ accordion ที่ระบุ
            handlePanelChange(panelKey)({ preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent);
  };

  return (
    <ClickAwayListener onClickAway={closeDropdown}>
      <FilterContainer ref={containerRef}>
        <FilterButton onClick={toggleDropdown}>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="18" 
            height="14" 
            viewBox="0 0 18 14" 
            fill="none" 
            style={{ marginTop: -1 }}
          >
            <path d="M1 1H17" stroke="#919192" strokeWidth="2" strokeLinecap="round" />
            <path d="M1 7H14" stroke="#919192" strokeWidth="2" strokeLinecap="round" />
            <path d="M1 13H11" stroke="#919192" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>{t('filters')}</span>
        </FilterButton>

        {isOpen && (
          <FilterContent>
            {/* Loading State */}
            {enableApiFilters && showLoadingState && isApiLoading ? (
              <LoadingState />
            ) : (
              <>
                {/* Error State - ปิดการแสดงข้อความ fallback */}
                <ErrorState
                  isApiError={isApiError}
                  fallbackToMockData={fallbackToMockData}
                  showApiErrorMessage={false}
                  apiError={apiError}
                />

                <ScrollableContent>
                  {/* Dynamic Filter Rendering */}
                  {dynamicFilters.map((filter) => {
                    const panelKey = filter.componentType === 'generic' 
                      ? `generic_${filter.group.group_id}` 
                      : filter.componentType;
                    
                    return (
                      <DynamicFilterRenderer
                        key={filter.group.group_id}
                        filter={filter}
                        isExpanded={expandedPanels.has(panelKey)}
                        onToggle={handlePanelChange(panelKey)}
                        onCollapseAfterSelect={handleCollapseAfterSelect(panelKey)}
                        priceMin={priceMin || undefined}
                        priceMax={priceMax || undefined}
                        minInputValue={minInputValue}
                        maxInputValue={maxInputValue}
                        priceErrorMessage={priceErrorMessage}
                        priceRangesMin={priceRangesMin}
                        priceRangesMax={priceRangesMax}
                        onMinInputChange={onMinInputChange}
                        onMaxInputChange={onMaxInputChange}
                        onMinPriceSelect={onMinPriceSelect}
                        onMaxPriceSelect={onMaxPriceSelect}
                        onMinInputClear={onMinInputClear}
                        onMaxInputClear={onMaxInputClear}
                        selectedBedrooms={selectedBedrooms?.toString() || null}
                        onBedroomSelect={(bedroom: string | null) => onBedroomSelect?.(bedroom ? parseInt(bedroom) : null)}
                        selectedBathrooms={selectedBathrooms?.toString() || null}
                        onBathroomSelect={(bathroom: string | null) => onBathroomSelect?.(bathroom ? parseInt(bathroom) : null)}
                        areaMin={areaMin || undefined}
                        areaMax={areaMax || undefined}
                        areaMinInput={areaMinInput}
                        areaMaxInput={areaMaxInput}
                        areaRangesMin={areaRangesMin}
                        areaRangesMax={areaRangesMax}
                        onAreaMinInputChange={onAreaMinInputChange}
                        onAreaMaxInputChange={onAreaMaxInputChange}
                        onAreaMinSelect={onAreaMinSelect}
                        onAreaMaxSelect={onAreaMaxSelect}
                        onAreaMinInputClear={onAreaMinInputClear}
                        onAreaMaxInputClear={onAreaMaxInputClear}
                        selectedQuota={selectedQuota}
                        onQuotaSelect={onQuotaSelect}
                        selectedStatus={selectedStatus}
                        onStatusSelect={onStatusSelect}
                        selectedFloor={selectedFloor}
                        onFloorSelect={onFloorSelect}
                        selectedView={selectedView}
                        onViewSelect={onViewSelect}
                        selectedDirection={selectedDirection}
                        onDirectionSelect={onDirectionSelect}
                        selectedPublishedBy={selectedPublishedBy}
                        onPublishedBySelect={onPublishedBySelect}
                        selectedPostedDate={selectedPostedDate}
                        onPostedDateSelect={onPostedDateSelect}
                        genericFilterSelections={genericFilterSelections}
                        onGenericFilterSelect={handleGenericFilterSelectWithCallback}
                        getFilterLayout={getFilterLayout}
                        lang={locale}
                      />
                    );
                  })}
                </ScrollableContent>

                {/* Action Buttons */}
                <ActionButtonContainer>
                  <ActionButton className="outlined" onClick={handleReset}>
                    {t('reset')}
                  </ActionButton>
                  <ActionButton className="contained" onClick={handleApply}>
                    {t('confirm')}
                  </ActionButton>
                </ActionButtonContainer>
              </>
            )}
          </FilterContent>
        )}
      </FilterContainer>
    </ClickAwayListener>
  );
};

export default FilterDropdown; 